import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import Papa from 'papaparse';
import * as Sentry from '@sentry/react';

import { UserContext } from '@/components/App';
import { ADD_CORP_CARRIERS, MATCH_CORP_CARRIERS } from '@/constants/apiRoutes';
import { post } from '@/utilities/onwardClient';
import { useClientUser } from '@/hooks';

import {
    GET_CORP_INTEGRATION_OVERRIDES,
    GET_CORP_SHIPPER_PARTNERSHIPS,
    GET_ORG_CONTRACTOR_PARTNERSHIPS,
} from '../../graphql';
import { GET_ORG_CARRIER_ONBOARDING } from '../../Onboarding/graphql';
import { CorpAdminContext } from '../../store';
import { CONFLICT_ACTIONS, DEFAULT_LEG } from './constants';
import {
    allConflictsResolved,
    buildManualRow,
    plannedCounts,
    noUsableRowsMessage,
    commitRowFor,
    commitRowForConflict,
    commitSeverity,
    isManualRowSubmittable,
    parseCarrierCsvRows,
    readErrorsNote,
    resolveAirportCoverage,
    splitMatchedRows,
    summarizeCommit,
    unknownAirportCodesInCsvRows,
} from './utils';

const EMPTY_MANUAL_FORM = {
    business_name: '',
    alias: '',
    dot_number: '',
    email: '',
    airport_codes: '',
    leg: DEFAULT_LEG,
};

/**
 * State and side effects for the Add Carriers drawer.
 *
 * Both intake methods run the same two-step flow — match, then commit — so a DOT
 * collision is surfaced before anything is written, whether it came from a 400-row
 * CSV or from one typed invite.
 */
const useAddCarriers = ({ open, onClose }) => {
    const { user, refetchAdminClients } = useContext(UserContext);
    const { organization_id } = useClientUser();
    const { callbacks } = useContext(CorpAdminContext);
    const { setNotification } = callbacks;
    const apolloClient = useApolloClient();

    const [file, setFile] = useState(null);
    const [parseError, setParseError] = useState('');
    const [dropzoneKey, setDropzoneKey] = useState(0);
    const [manualForm, setManualForm] = useState(EMPTY_MANUAL_FORM);
    // Leg for the upload as a whole. The template has no Leg column, so this is how a
    // CSV says whether it holds middle-mile, final-mile or contract carriers; a file
    // that does carry the column overrides it per row.
    const [csvLeg, setCsvLeg] = useState(DEFAULT_LEG);
    const [busy, setBusy] = useState(false);
    // True only while the commit request is out. The server is writing carriers by
    // then, so the drawer and modal refuse to close until it answers — closing used to
    // drop the result, leaving carriers created with no toast and a stale table.
    const [committing, setCommitting] = useState(false);

    // Rows held between the match call and the commit: `conflicts` drives the Upload
    // Exceptions modal, while `ready` and the rows we sent ride along so a single
    // commit covers the whole upload once the conflicts are settled.
    const [pending, setPending] = useState(null);
    const [resolutions, setResolutions] = useState({});

    // Bumped on every submit/commit and again on close(), so an in-flight match/commit
    // request can tell, once it resolves, whether the drawer it was launched from is
    // still the one open — otherwise a request outliving a close (or a cancelled one
    // superseded by a new submit) would still act on a stale result: reopening the
    // exceptions modal over an already-closed drawer, or double-firing a notification.
    const requestIdRef = useRef(0);

    // Drops the chosen file and remounts the dropzone — react-dropzone keeps its accepted
    // file internally, so remounting is how the SKU importer clears it too.
    //
    // Deliberately does NOT clear parseError. The failure paths set a message and then
    // clear the file, and both setters land in one React batch: clearing the error here
    // wiped the message before it could ever render, which looked like the button doing
    // nothing at all.
    const clearFile = useCallback(() => {
        setFile(null);
        setDropzoneKey((k) => k + 1);
    }, []);

    const reset = useCallback(() => {
        clearFile();
        setParseError('');
        setManualForm(EMPTY_MANUAL_FORM);
        setCsvLeg(DEFAULT_LEG);
        setPending(null);
        setResolutions({});
        setBusy(false);
        setCommitting(false);
    }, [clearFile]);

    const close = useCallback(() => {
        if (committing) return;
        // Invalidate whatever request is in flight before touching state, so its
        // eventual resolution is a no-op instead of reopening the exceptions modal or
        // committing on top of a closed drawer.
        requestIdRef.current += 1;
        reset();
        onClose?.();
    }, [committing, reset, onClose]);

    const notify = useCallback((severity, message) => setNotification?.({ severity, message }), [setNotification]);

    /** POST the rows to the match endpoint and return the split result (no state writes —
     * the caller decides whether the result is still relevant before acting on it). */
    const runMatch = useCallback(
        async (rows) => {
            if (!rows.length) {
                notify('warning', 'No usable rows found in that file.');
                return null;
            }
            // organization_id is only read by the emulator, where isAuthenticated
            // short-circuits and leaves no token to derive it from. Deployed
            // environments take it from the caller's token and ignore this.
            const { data } = await post(MATCH_CORP_CARRIERS, { rows, organization_id });
            return splitMatchedRows(data?.rows);
        },
        [notify, organization_id]
    );

    /**
     * POST the settled rows. Errors from the match step are reported but never sent —
     * the backend would only reject them again.
     *
     * Once the POST answers, the result is always reported and the table refreshed:
     * the carriers are written either way, and a silent commit sends the operator to
     * re-upload and duplicate them. The callers' requestId checks only gate UI state.
     */
    const runCommit = useCallback(
        async ({
            ready = [],
            conflicts = [],
            conflictResolutions = {},
            sourceRows = [],
            notePrefix = '',
            requestId,
        }) => {
            const rows = [
                ...ready.map((r) => commitRowFor(r, sourceRows[r.index])),
                ...conflicts.map((c) => commitRowForConflict(conflictResolutions[c.index], sourceRows[c.index])),
            ];
            if (!rows.length) {
                if (requestId === requestIdRef.current) notify('warning', 'Nothing left to add.');
                return;
            }

            let refreshFailed = false;
            let data;
            setCommitting(true);
            try {
                ({ data } = await post(ADD_CORP_CARRIERS, {
                    rows,
                    organization_id,
                    inviter_name: user?.business_name || user?.username || undefined,
                }));
            } finally {
                setCommitting(false);
            }
            const counts = data?.counts || {};

            // Invited carriers land under this organization (the client list), and
            // connected ones — plus invited contract carriers — show up through the
            // org's overrides and partnerships, so all three are refetched for the
            // table to reflect the commit. The Onboarding View's query is refetched
            // too, since the drawer also opens over it.
            //
            // Non-fatal: the carriers are already written by this point. Letting a
            // refetch failure propagate reported a successful commit as "Error adding
            // carriers" and left the drawer open, which is worse than a stale table the
            // operator can fix by reloading.
            if (counts.invited || counts.created || counts.connected) {
                try {
                    await Promise.all([
                        refetchAdminClients?.(),
                        apolloClient.refetchQueries({
                            include: [
                                GET_CORP_INTEGRATION_OVERRIDES,
                                GET_CORP_SHIPPER_PARTNERSHIPS,
                                GET_ORG_CONTRACTOR_PARTNERSHIPS,
                                GET_ORG_CARRIER_ONBOARDING,
                            ],
                        }),
                    ]);
                } catch (err) {
                    console.error('Carriers were added but the account list could not be refreshed', err);
                    Sentry.captureException(err);
                    refreshFailed = true;
                }
            }

            const failures = (data?.results || []).filter((r) => r.status === 'error');
            const explainedSkip = (data?.results || []).find((r) => r.status === 'skipped' && r.reason);

            // Nothing on this page lists per-row results, so the first failure's reason
            // rides along with the summary — otherwise "1 row failed" is a dead end.
            const summary = summarizeCommit(counts);
            const detail = failures.length
                ? ` — ${failures[0].business_name || 'a row'}: ${failures[0].error}`
                : refreshFailed
                ? ' — reload the page to see them'
                : explainedSkip
                ? ` — ${explainedSkip.business_name || 'a row'}: ${explainedSkip.reason}`
                : '';
            // notePrefix carries forward a warning from the match step (unreadable rows,
            // unresolved airport codes) that would otherwise be clobbered by this single-
            // slot notification — folded in here instead of firing its own toast first.
            const prefix = notePrefix ? `${notePrefix}; ` : '';
            const severity = failures.length
                ? commitSeverity(counts)
                : notePrefix || explainedSkip
                ? 'warning'
                : 'success';
            notify(severity, `${prefix}${summary}${detail}`);

            if (failures.length) console.error('Add carriers row failures', failures);
        },
        [notify, refetchAdminClients, apolloClient, user, organization_id]
    );

    /** Match → commit, raising the exceptions modal in between when needed. */
    const submitRows = useCallback(
        async (rows, { unknownAirportCodes = [] } = {}) => {
            const requestId = ++requestIdRef.current;
            setBusy(true);
            try {
                const split = await runMatch(rows);
                if (requestId !== requestIdRef.current) return;
                if (!split) return;

                const notes = [];
                if (split.errors.length) notes.push(readErrorsNote(split.errors));
                if (unknownAirportCodes.length) {
                    notes.push(
                        `unrecognized airport code${
                            unknownAirportCodes.length === 1 ? '' : 's'
                        } ignored: ${unknownAirportCodes.join(', ')}`
                    );
                }
                const note = notes.join('; ');

                // Every row failed to read and nothing else is pending — this is the
                // only notification this run will fire, so show it now.
                if (split.errors.length && !split.ready.length && !split.conflicts.length) {
                    notify('error', note);
                    return;
                }

                // Hold the rows we sent alongside the verdicts — the commit step and the
                // exceptions modal both read fields off them, indexed by row position.
                setPending({ ...split, sourceRows: rows, note });
                setResolutions({});

                // Conflicts need a human; the modal commits the whole batch on confirm,
                // carrying `note` forward so it lands in that commit's notification.
                if (split.conflicts.length) return;
                // Every remaining row failed to read (no conflicts either) — returning
                // here keeps the reason on screen rather than replacing it below.
                if (!split.ready.length) return;

                await runCommit({ ready: split.ready, sourceRows: rows, notePrefix: note, requestId });
                if (requestId !== requestIdRef.current) return;
                reset();
                onClose?.();
            } catch (err) {
                if (requestId !== requestIdRef.current) return;
                Sentry.captureException(err);
                console.error(err);
                notify('error', err?.response?.data?.error || 'Error adding carriers');
            } finally {
                if (requestId === requestIdRef.current) setBusy(false);
            }
        },
        [runMatch, runCommit, notify, reset, onClose]
    );

    const handleFileAdd = useCallback((added) => {
        setParseError('');
        setFile(added);
    }, []);

    const parseAndReview = useCallback(() => {
        // Papa.parse reads the file asynchronously, and `busy` doesn't flip until
        // submitRows runs from its `complete` callback — set it synchronously here so a
        // second click during a slow parse can't start a second match+commit for the
        // same file.
        if (!file || busy) return;
        setBusy(true);
        setParseError('');
        Papa.parse(file, {
            header: true,
            skipEmptyLines: 'greedy',
            complete: (result) => {
                const rows = parseCarrierCsvRows(result.data, csvLeg);
                if (!rows.length) {
                    // Name the headers we actually saw. "No usable rows" on its own is a
                    // dead end when the real problem is that the file's columns don't
                    // match the template.
                    setParseError(noUsableRowsMessage(result.meta?.fields));
                    clearFile();
                    setBusy(false);
                    return;
                }
                submitRows(rows, { unknownAirportCodes: unknownAirportCodesInCsvRows(result.data) });
            },
            error: (err) => {
                console.error(err);
                setParseError('Could not read the .csv file.');
                clearFile();
                setBusy(false);
            },
        });
    }, [file, busy, csvLeg, clearFile, submitRows]);

    const setManualField = useCallback(
        (key) => (e) => setManualForm((prev) => ({ ...prev, [key]: e.target.value })),
        []
    );

    // Codes the operator typed that we can't turn into coverage, surfaced under the
    // field rather than dropped.
    const unknownAirportCodes = useMemo(
        () => resolveAirportCoverage(manualForm.airport_codes).unknown,
        [manualForm.airport_codes]
    );

    const sendInvite = useCallback(() => {
        if (busy || !isManualRowSubmittable(manualForm)) return;
        submitRows([buildManualRow(manualForm)]);
    }, [busy, manualForm, submitRows]);

    // --- Upload Exceptions modal --------------------------------------------

    const resolveConflict = useCallback(
        (index, patch) => setResolutions((prev) => ({ ...prev, [index]: { ...prev[index], ...patch } })),
        []
    );

    // Skip and Add Anyway toggle: picking the action a row already has clears it, which
    // returns the row to undecided and re-disables the commit button. Without this a
    // misclick can only be undone by cancelling the whole upload.
    const toggleConflictAction = useCallback(
        (index, action) =>
            setResolutions((prev) => ({
                ...prev,
                [index]: {
                    ...prev[index],
                    action: prev[index]?.action === action ? undefined : action,
                    // Only Connect carries a target account.
                    client_id: undefined,
                },
            })),
        []
    );

    const skipConflict = useCallback(
        (index) => toggleConflictAction(index, CONFLICT_ACTIONS.SKIP),
        [toggleConflictAction]
    );

    const addConflictAnyway = useCallback(
        (index) => toggleConflictAction(index, CONFLICT_ACTIONS.ADD_ANYWAY),
        [toggleConflictAction]
    );

    // Connect names which existing account to link, so it toggles on the pair: picking
    // the same account again clears it, picking a different one switches to it.
    const connectConflict = useCallback(
        (index, client_id) =>
            setResolutions((prev) => {
                const current = prev[index];
                const same = current?.action === CONFLICT_ACTIONS.CONNECT && current.client_id === client_id;
                return {
                    ...prev,
                    [index]: {
                        ...current,
                        action: same ? undefined : CONFLICT_ACTIONS.CONNECT,
                        client_id: same ? undefined : client_id,
                    },
                };
            }),
        []
    );

    const commitConflicts = useCallback(async () => {
        if (!pending) return;

        // Every row skipped: SKIP is a no-op on the server, so posting the batch would
        // only round-trip to be told nothing happened.
        const { adds, connects } = plannedCounts(pending.ready, pending.conflicts, resolutions);
        if (adds + connects === 0) {
            notify('info', 'Nothing added — every row was skipped');
            reset();
            onClose?.();
            return;
        }

        const requestId = ++requestIdRef.current;
        setBusy(true);
        try {
            await runCommit({
                ready: pending.ready,
                conflicts: pending.conflicts,
                conflictResolutions: resolutions,
                sourceRows: pending.sourceRows,
                notePrefix: pending.note,
                requestId,
            });
            if (requestId !== requestIdRef.current) return;
            reset();
            onClose?.();
        } catch (err) {
            if (requestId !== requestIdRef.current) return;
            Sentry.captureException(err);
            console.error(err);
            notify('error', err?.response?.data?.error || 'Error adding carriers');
        } finally {
            if (requestId === requestIdRef.current) setBusy(false);
        }
    }, [pending, resolutions, runCommit, notify, reset, onClose]);

    const dismissConflicts = useCallback(() => {
        if (committing) return;
        // Backing out of the exceptions modal abandons the whole batch rather than
        // silently committing the unambiguous half — the operator asked to stop.
        setPending(null);
        setResolutions({});
        setParseError('');
        clearFile();
    }, [committing, clearFile]);

    return {
        state: {
            open,
            busy,
            committing,
            file,
            parseError,
            dropzoneKey,
            manualForm,
            csvLeg,
            unknownAirportCodes,
            manualValid: isManualRowSubmittable(manualForm),
            organization_id,
            conflicts: pending?.conflicts || [],
            sourceRows: pending?.sourceRows || [],
            conflictsOpen: !!pending?.conflicts?.length,
            planned: plannedCounts(pending?.ready, pending?.conflicts, resolutions),
            resolutions,
            allResolved: allConflictsResolved(pending?.conflicts, resolutions),
        },
        callbacks: {
            close,
            setCsvLeg: (e) => setCsvLeg(e.target.value),
            handleFileAdd,
            parseAndReview,
            setManualField,
            sendInvite,
            resolveConflict,
            skipConflict,
            addConflictAnyway,
            connectConflict,
            commitConflicts,
            dismissConflicts,
        },
    };
};

export default useAddCarriers;
